import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import L, { LatLngExpression } from 'leaflet';
import icons from './utils/icons';
import { DEFAULT_CENTER, DEFAULT_ZOOM } from './constants';
import './leaflet.scss';
import './leaflet.draw.scss';

function CenterButtonControl({
  map,
  defaultCenter = DEFAULT_CENTER,
  defaultZoom = DEFAULT_ZOOM,
  featureGroupRef,
  onCenterMap,
}: {
  map: L.Map;
  defaultCenter?: LatLngExpression | null;
  defaultZoom?: number;
  featureGroupRef: React.RefObject<L.FeatureGroup | null>;
  onCenterMap?: () => void;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    const CustomButton = L.Control.extend({
      onAdd() {
        const btn = L.DomUtil.create('button', 'leaflet-bar-center');
        btn.innerHTML = icons.centerIcon;
        btn.title = t('center-map');

        L.DomEvent.on(btn, 'click', (e) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);

          if (onCenterMap) {
            onCenterMap();
          } else {
            const group = featureGroupRef.current;
            if (!group) return;

            const bounds = group.getBounds();
            if (bounds.isValid()) {
              map.fitBounds(bounds, {
                padding: [20, 20],
                maxZoom: 15,
              });
            }
          }
        });

        return btn;
      },
      onRemove() {
        return undefined;
      },
    });

    const control = new CustomButton({ position: 'topleft' });
    control.addTo(map);

    const controlContainer = map
      .getContainer()
      .querySelector('.leaflet-top.leaflet-left');
    const btnEl = control.getContainer();
    if (controlContainer && btnEl) {
      // Center button should be after zoom controls
      const zoomControls = controlContainer.querySelector(
        '.leaflet-control-zoom',
      );
      if (zoomControls) {
        controlContainer.insertBefore(btnEl, zoomControls.nextSibling);
      } else {
        controlContainer.appendChild(btnEl);
      }
    }

    return () => {
      control.remove();
    };
  }, [map, t, defaultCenter, defaultZoom, featureGroupRef, onCenterMap]);

  return null;
}

export default memo(CenterButtonControl);
